/**
 * SPAACIO Recently Viewed Products
 * Sistema de tracking de productos vistos recientemente
 *
 * v2.0.0 - Hidratación en tiempo real
 *   - Los handles persisten en localStorage (sin cachear precios)
 *   - Precios/stock/imagen se obtienen frescos desde /products/{handle}.js
 *   - Resuelve el bug de "precios obsoletos" durante Hot Sale, Buen Fin, etc.
 *   - Datos cacheados se mantienen solo como fallback si el fetch falla
 *
 * Funciona con localStorage para visitantes anónimos.
 * El localStorage almacena: handle + fallback data (por si la red falla).
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'spaacio_recently_viewed';
  const MAX_PRODUCTS = 20;
  const EXPIRATION_DAYS = 30;

  /**
   * Obtiene la lista de productos vistos del localStorage (datos crudos cacheados)
   * @returns {Array} Lista de productos (vacío si no hay o hay error)
   */
  function getViewedProducts() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];

      const products = JSON.parse(raw);
      if (!Array.isArray(products)) return [];

      // Filtrar productos expirados
      const now = Date.now();
      const expirationMs = EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
      return products.filter(p => p && p.timestamp && (now - p.timestamp) < expirationMs);
    } catch (e) {
      console.warn('[SPAACIO RecentlyViewed] Error reading storage:', e);
      return [];
    }
  }

  /**
   * Guarda la lista de productos en localStorage
   * @param {Array} products
   */
  function saveViewedProducts(products) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
    } catch (e) {
      console.warn('[SPAACIO RecentlyViewed] Error saving storage:', e);
    }
  }

  /**
   * Agrega un producto a la lista (o lo mueve al inicio si ya existe)
   * Guardamos los datos básicos como fallback por si la red falla al renderizar.
   * @param {Object} product
   */
  function trackProduct(product) {
    if (!product || !product.handle) {
      console.warn('[SPAACIO RecentlyViewed] Invalid product data');
      return;
    }

    let products = getViewedProducts();
    products = products.filter(p => p.handle !== product.handle);

    products.unshift({
      handle: product.handle,
      title: product.title || '',
      price: product.price || 0,
      compare_at_price: product.compare_at_price || 0,
      image: product.image || '',
      url: product.url || '',
      vendor: product.vendor || '',
      available: product.available !== false,
      timestamp: Date.now()
    });

    if (products.length > MAX_PRODUCTS) {
      products = products.slice(0, MAX_PRODUCTS);
    }

    saveViewedProducts(products);

    document.dispatchEvent(new CustomEvent('spaacio:product-viewed', {
      detail: { product, allProducts: products }
    }));
  }

  /**
   * Obtiene productos para mostrar (excluye el actual si estamos en una PDP)
   * NO hidrata aquí - devuelve datos cacheados crudos. Para datos frescos
   * usa getDisplayHydrated().
   *
   * @param {string} excludeHandle - Handle a excluir (producto actual)
   * @param {number} limit - Máximo de productos a devolver
   * @returns {Array}
   */
  function getProductsToDisplay(excludeHandle, limit) {
    let products = getViewedProducts();
    if (excludeHandle) {
      products = products.filter(p => p.handle !== excludeHandle);
    }
    if (limit && limit > 0) {
      products = products.slice(0, limit);
    }
    return products;
  }

  /**
   * Obtiene productos HIDRATADOS (con precios/stock frescos desde Shopify).
   * Esta es la función que las secciones deben usar para renderizar.
   *
   * Si SpaacioProductHydrator no está disponible (no se cargó el módulo),
   * cae al comportamiento legacy de datos cacheados.
   *
   * @param {string} excludeHandle
   * @param {number} limit
   * @returns {Promise<Array>}
   */
  async function getProductsToDisplayHydrated(excludeHandle, limit) {
    const cached = getProductsToDisplay(excludeHandle, limit);
    if (!cached.length) return [];

    // Si el hidratador no está disponible, devolver datos cacheados (legacy fallback)
    if (!window.SpaacioProductHydrator) {
      console.warn('[SPAACIO RecentlyViewed] Hydrator no disponible, usando cache');
      return cached;
    }

    try {
      const hydrated = await window.SpaacioProductHydrator.hydrateFromCache(cached);
      return hydrated;
    } catch (e) {
      console.warn('[SPAACIO RecentlyViewed] Error hidratando, fallback a cache:', e);
      return cached;
    }
  }

  /**
   * Limpia todos los productos
   */
  function clearProducts() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      document.dispatchEvent(new CustomEvent('spaacio:recently-viewed-cleared'));
    } catch (e) {
      console.warn('[SPAACIO RecentlyViewed] Error clearing:', e);
    }
  }

  /**
   * Auto-tracking en PDP
   */
  function autoTrack() {
    const dataEl = document.getElementById('sp-current-product-data');
    if (!dataEl) return;

    try {
      const productData = JSON.parse(dataEl.textContent);
      if (productData && productData.handle) {
        trackProduct(productData);
      }
    } catch (e) {
      console.warn('[SPAACIO RecentlyViewed] Error parsing product data:', e);
    }
  }

  // API pública
  window.SpaacioRecentlyViewed = {
    track: trackProduct,
    getAll: getViewedProducts,
    getDisplay: getProductsToDisplay,                    // sincrónico, datos cacheados (legacy)
    getDisplayHydrated: getProductsToDisplayHydrated,    // ✨ NUEVO: async, datos frescos
    clear: clearProducts,
    STORAGE_KEY: STORAGE_KEY
  };

  // Inicializar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoTrack, { once: true });
  } else {
    autoTrack();
  }
})();